import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { authApi } from "../../services/authService";
import { getErrorMessage } from "../../utils/errors";
import { Input } from "../../components/common/Input/Input";
import { Button } from "../../components/common/Button/Button";
import "./Auth.css";

function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const validate = (): boolean => {
    if (!password) {
      setError(t("auth.validation.passwordRequired"));
      return false;
    }
    if (password.length < 6) {
      setError(t("auth.validation.passwordMin"));
      return false;
    }
    if (password !== confirmPassword) {
      setError(t("auth.passwordsMustMatch"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");

    if (!token) {
      setError(t("auth.invalidToken"));
      return;
    }

    if (!validate()) return;

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(
        await getErrorMessage(
          err,
          t("auth.resetFailed") ||
            "Failed to reset password. Please request a new link.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-card">
        <img src="/logo.png" alt="FCS" className="auth-logo" />
        <h1>{t("auth.resetPassword")}</h1>
        <p className="auth-subtitle">{t("auth.resetPasswordSubtitle")}</p>

        {error && <p className="auth-error">{error}</p>}

        {success ? (
          <div className="auth-success">
            <svg
              viewBox="0 0 24 24"
              width="48"
              height="48"
              fill="none"
              stroke="var(--color-success)"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12l2.5 2.5L16 9" />
            </svg>
            <p>{t("auth.resetSuccess")}</p>
          </div>
        ) : !token ? (
          <p className="auth-error">{t("auth.invalidToken")}</p>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <Input
              id="password"
              type="password"
              label={t("auth.newPassword")}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              floating={true}
              className="form-group"
            />

            <Input
              id="confirmPassword"
              type="password"
              label={t("auth.confirmPassword")}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              floating={true}
              className="form-group"
            />

            <Button type="submit" className="auth-submit-btn" loading={loading}>
              {t("auth.resetPassword")}
            </Button>
          </form>
        )}

        <p className="auth-footer">
          <Link to="/login">{t("auth.backToLogin")}</Link>
        </p>
      </div>
    </main>
  );
}

export default ResetPassword;
